import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { ParentDelegatesListResponse, StudentsResponse } from '../types';

export const parentStudentKeys = {
  all: ['parent-students'] as const,
  bySchool: (schoolId: string) => ['parent-students', schoolId] as const,
};

export const parentDelegateKeys = {
  list: (studentId: string) => ['parent-delegates', studentId] as const,
};

export function useParentStudents(
  selectedSchoolId?: string,
  options?: Omit<UseQueryOptions<StudentsResponse>, 'queryKey' | 'queryFn'>,
) {
  const key = selectedSchoolId
    ? parentStudentKeys.bySchool(selectedSchoolId)
    : parentStudentKeys.all;
  const url = selectedSchoolId ? `/students?schoolId=${selectedSchoolId}` : '/students';
  return useQuery<StudentsResponse>(
    key,
    () => apiClient.get(url).then((r: { data: unknown }) => r.data as StudentsResponse),
    options,
  );
}

export function useLinkStudent(
  options?: UseMutationOptions<unknown, unknown, { studentId: string; schoolCode?: string }>,
) {
  const qc = useQueryClient();
  return useMutation(
    (payload: { studentId: string; schoolCode?: string }) =>
      apiClient.post('/students/link-by-id', payload).then((r: { data: unknown }) => r.data),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        qc.invalidateQueries('parent-students');
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
}

export function useParentDelegates(
  studentId: string | undefined,
  options?: Omit<UseQueryOptions<ParentDelegatesListResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ParentDelegatesListResponse>(
    parentDelegateKeys.list(studentId || ''),
    () =>
      apiClient
        .get(`/students/${studentId}/delegates`)
        .then((r: { data: unknown }) => r.data as ParentDelegatesListResponse),
    { enabled: Boolean(studentId), ...options },
  );
}

export function useAddParentDelegate(
  options?: UseMutationOptions<
    ParentDelegatesListResponse,
    unknown,
    { studentId: string; mobile: string; name?: string; canViewStudent?: boolean; canDispersal?: boolean }
  >,
) {
  const qc = useQueryClient();
  return useMutation(
    (payload: { studentId: string; mobile: string; name?: string; canViewStudent?: boolean; canDispersal?: boolean }) =>
      apiClient
        .post(`/students/${payload.studentId}/delegates`, {
          mobile: payload.mobile,
          name: payload.name,
          canViewStudent: payload.canViewStudent,
          canDispersal: payload.canDispersal,
        })
        .then((r: { data: unknown }) => r.data as ParentDelegatesListResponse),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        qc.invalidateQueries(parentDelegateKeys.list(variables.studentId));
        qc.invalidateQueries('parent-students');
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
}

export function useRemoveParentDelegate(
  options?: UseMutationOptions<ParentDelegatesListResponse, unknown, { studentId: string; delegateUserId: string }>,
) {
  const qc = useQueryClient();
  return useMutation(
    (payload: { studentId: string; delegateUserId: string }) =>
      apiClient
        .delete(`/students/${payload.studentId}/delegates/${payload.delegateUserId}`)
        .then((r: { data: unknown }) => r.data as ParentDelegatesListResponse),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        qc.invalidateQueries(parentDelegateKeys.list(variables.studentId));
        qc.invalidateQueries('parent-students');
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
}

export function usePatchParentDelegate(
  options?: UseMutationOptions<
    ParentDelegatesListResponse,
    unknown,
    { studentId: string; delegateUserId: string; canViewStudent?: boolean; canDispersal?: boolean }
  >,
) {
  const qc = useQueryClient();
  return useMutation(
    (payload: { studentId: string; delegateUserId: string; canViewStudent?: boolean; canDispersal?: boolean }) =>
      apiClient
        .patch(`/students/${payload.studentId}/delegates/${payload.delegateUserId}`, {
          canViewStudent: payload.canViewStudent,
          canDispersal: payload.canDispersal,
        })
        .then((r: { data: unknown }) => r.data as ParentDelegatesListResponse),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        qc.invalidateQueries(parentDelegateKeys.list(variables.studentId));
        qc.invalidateQueries('parent-students');
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
}
